<!--
  CampaignReviewLaunch.vue — Claude Design redesign experiment.

  The "landing" view of the redesigned editor. Shows:
   - Completion donut (% of sections done -- guidance) + a headline from
     the server's launch answer ("Store is live" / "Ready to launch" /
     "Not ready to launch yet")
   - ONE status block (Trello #554): the headline banner above, then two
     labelled lists. "Must fix to launch" is the server's launch reasons,
     each with a Fix link; "Recommended" is section guidance and optional
     setup. The headline, its count and the Launch button follow only the
     Must fix list.
   - Section cards grid (one card per section, status pill, summary)
   - Launch CTA, or a "Store is live" status pill on a live store. Both
     follow the SERVER's one can-launch answer (`launch` on the readiness
     payload, state_transition_guard.compute_store_launch_eligibility) --
     the same answer the Stores list toggle reads. The section checklist
     is guidance only; it does not decide the button.

  Section status comes from the parent (CampaignAddEdit) via the
  section-status prop. The parent computes status by tab-validity
  signals it already collects (incompleteTabs map).
-->
<template>
  <div class="crl" data-testid="review-launch-root">
    <div class="crl__status" data-testid="review-launch-status-block">
    <div class="crl__banner" :class="bannerClass">
      <div class="crl__donut">
        <svg width="84" height="84" viewBox="0 0 84 84">
          <circle cx="42" cy="42" r="36" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="8"></circle>
          <circle
            cx="42" cy="42" r="36"
            fill="none"
            stroke="#00d084"
            stroke-width="8"
            stroke-linecap="round"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="dashOffset"
            transform="rotate(-90 42 42)"
          ></circle>
        </svg>
        <div class="crl__donut-pct">{{ percentComplete }}%</div>
        <span class="crl__sr-only">{{ doneCount }} of {{ totalSections }} sections complete</span>
      </div>
      <div class="crl__copy">
        <h2 class="crl__h2" data-testid="review-launch-headline">{{ bannerHeadline }}</h2>
        <p class="crl__sub">{{ bannerSubline }}</p>
        <p v-if="scheduleLine" class="crl__sub" data-testid="review-launch-schedule">{{ scheduleLine }}</p>
      </div>
      <div class="crl__actions">
        <!-- A live store has nothing to launch: a status, not a dimmed
             button (Jake, dev, 2026-10-22). -->
        <div
          v-if="isLive"
          class="crl__live"
          role="status"
          data-cy="crl-live"
          data-testid="review-launch-live-status"
        >
          <span class="tf-icon">check_circle</span>Store is live
        </div>
        <button
          v-else
          class="crl__launch"
          :disabled="!canLaunch"
          @click="$emit('launch')"
          data-cy="crl-launch"
          data-testid="review-launch-launch-btn"
          :data-can-launch="canLaunch ? 'true' : 'false'"
          :title="canLaunch ? '' : launchDisabledTooltip"
        >
          <span class="tf-icon">rocket_launch</span>Launch Store
        </button>
        <button
          class="crl__preview"
          @click="$emit('preview')"
          data-cy="crl-preview"
          data-testid="review-launch-preview-btn"
        >
          <span class="tf-icon">visibility</span>Preview Storefront
        </button>
      </div>
    </div>

    <div
      v-if="hasStoreId"
      class="crl__status-body"
      :class="{ 'crl__status-body--empty': !hasBodyContent }"
    >
      <div
        v-if="readinessError"
        class="crl__readiness-error"
        data-testid="review-launch-readiness-error"
      >
        <span class="tf-icon">error_outline</span>
        <div>
          <strong>Launch readiness could not be checked.</strong>
          <p class="crl__readiness-error-sub">
            We couldn't verify whether this store can launch, so Launch Store
            stays off until the check succeeds. Reload the page to try again,
            or review artwork approval in the
            <a class="crl__link" @click.prevent="goToApprovalWorkspace">
              Approval Workspace
            </a>.
          </p>
        </div>
      </div>
      <launch-status-lists
        v-else
        :must-fix="mustFixRows"
        :attention="attentionRows"
        :recommended="recommendedList"
        :loading="readinessLoading"
        data-testid="review-launch-readiness-card"
        @fix="followFix"
      />
    </div>
    </div>

    <!--
      Bug Intake #524: fixed-price sizes below (estimated) cost or under the
      margin floor. Advisory only -- it never changes the Launch button.
    -->
    <fixed-price-margin-warning
      v-if="campaign && campaign._jv && campaign._jv.id"
      class="crl__readiness"
      :store-id="campaign._jv.id"
    />

    <div class="crl__section-list-head">
      <h2 class="crl__h2-sm">All sections</h2>
    </div>

    <div class="crl__grid">
      <a
        v-for="card in sectionCards"
        :key="card.id"
        class="crl__card"
        :class="cardClass(card)"
        @click.prevent="$emit('go-to-section', card.id)"
        :data-cy="`crl-card-${card.id}`"
        :data-testid="`review-launch-card-${card.id}`"
      >
        <div class="crl__card-top">
          <div class="crl__card-icon"><span class="tf-icon">{{ card.icon }}</span></div>
          <div class="crl__card-h">{{ card.title }}</div>
          <div class="crl__card-status" :class="statusClass(card)">
            {{ statusLabel(card) }}
          </div>
        </div>
        <div
          v-if="cardSubtitle(card)"
          class="crl__card-sub"
          :data-cy="`crl-card-sub-${card.id}`"
          :data-testid="`review-launch-card-sub-${card.id}`"
        >
          {{ cardSubtitle(card) }}
        </div>
        <span class="tf-icon crl__card-arrow">arrow_forward</span>
      </a>
    </div>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { SECTION_META } from "./sectionRoutes.js";
import {
  campaignHasOwnZone,
  campaignScheduleZone,
  fetchPlatformTimezone,
  windowMomentLabel,
} from "./storeWindow.js";
import LaunchStatusLists from "./readiness/LaunchStatusLists.vue";
import { reasonRows, recommendedRows } from "./readiness/launchStatusRows";
import FixedPriceMarginWarning from "./readiness/FixedPriceMarginWarning.vue";
import { useStoreReadinessStore } from "@/stores/storeReadiness";
import { unreadySections } from "./unreadySections";

// Single source of truth for the editable sections rendered as cards.
// Imported from sectionRoutes.js so the sidebar (StoreEditShell) and the
// grid (here) stay in lock-step. The pre-rearch 11-card vocabulary
// (Name & Launch / Client Info / Departments / Payment & Credits /
// Managers / Fulfillment) is gone -- the locked Phase-1 IA is 10 sections
// matching the nested-route ids in /distributor/stores/:id/edit/:section.
const ALL_SECTIONS = SECTION_META;

export default {
  name: "StoreReviewLaunch",
  components: {
    "launch-status-lists": LaunchStatusLists,
    "fixed-price-margin-warning": FixedPriceMarginWarning,
  },
  props: {
    campaign:        { type: Object, required: true },
    sectionStatus:   { type: Object, default: () => ({}) },
    sectionSubtitle: { type: Object, default: () => ({}) },
  },
  emits: ['go-to-section', 'launch', 'preview'],
  data() {
    return {
      // undefined = not looked up yet; null = none set / lookup failed.
      platformTimezone: undefined,
    };
  },
  mounted() {
    // The platform default zone, only when the distributor names none.
    if (campaignHasOwnZone(this.campaign)) {
      this.platformTimezone = null;
    } else {
      fetchPlatformTimezone(this.$axios).then((tz) => { this.platformTimezone = tz; });
    }
  },
  watch: {
    "campaign._jv.id": {
      immediate: true,
      handler(newId) {
        if (newId) this.loadReadiness(newId);
      },
    },
  },
  computed: {
    // The backend's launchability verdict, READ FROM the shared
    // storeReadiness Pinia store rather than copied into local data on
    // fetch.
    //
    // This used to be a local snapshot written only by loadReadiness().
    // Because StoreEditShell renders the review pane with ``v-show``, not
    // ``v-if``, this component stays MOUNTED for the whole life of the
    // editor and its ``campaign._jv.id`` watcher fires exactly once -- so
    // a snapshot taken on first paint was the one the distributor kept
    // looking at. Any refresh triggered from elsewhere (the shell
    // re-asking after the decorator is chosen on the Decorations page)
    // landed in the store and was never seen here, which is half of the
    // stale-decorator bug (Jake, 8083, 2026-08-24).
    //
    // Reading the store directly means there is ONE copy of this fact and
    // every writer of it reaches the screen -- in both directions: a
    // store that BECOMES unlaunchable must re-disable Launch, not keep an
    // enabled button from a happier earlier fetch.
    readinessPayload() {
      const storeId = this.campaign?._jv?.id;
      if (storeId == null) return null;
      return useStoreReadinessStore().readinessFor(storeId);
    },
    // "Opens Oct 7, 9:00 AM EDT. Closes Oct 31, 2:30 PM EDT." -- a Pop-Up
    // only (a Permanent store has no schedule). Held back until the zone the
    // times are read in is known (card #497).
    scheduleLine() {
      const c = this.campaign;
      if (!c || c.is_permanent_store || !c.start_date) return '';
      if (!campaignHasOwnZone(c) && this.platformTimezone === undefined) return '';
      const tz = campaignScheduleZone(c, this.platformTimezone);
      const opens = windowMomentLabel(c.start_date, tz, { withDate: true });
      const closes = windowMomentLabel(c.end_date, tz, { withDate: true });
      if (!opens) return '';
      return closes ? `Opens ${opens}. Closes ${closes}.` : `Opens ${opens}.`;
    },
    sectionCards() { return ALL_SECTIONS; },
    totalSections() { return ALL_SECTIONS.length; },
    hasStoreId() { return !!(this.campaign && this.campaign._jv && this.campaign._jv.id); },
    launchGates() {
      const gates = this.readinessPayload?.launch_gates;
      return Array.isArray(gates) ? gates : [];
    },
    // MUST FIX: the server's launch reasons, worded and linked. Empty on a
    // live store (nothing to launch) and until the server has answered.
    mustFixRows() {
      return reasonRows(this.launchReasons, this.launchGates);
    },
    // A live store's open items (artwork, digitization): real, but not
    // launch blockers any more, so they are not "must fix".
    attentionRows() {
      if (!this.isLive) return [];
      return reasonRows(this.readinessPayload?.blockers, this.launchGates);
    },
    recommendedList() {
      if (!this.launchAnswer) return [];
      const mustFixSectionIds = new Set(
        this.launchReasons
          .filter((r) => r.code === "sections_incomplete")
          .flatMap((r) => unreadySections(r.tabs).map((s) => s.id))
      );
      return recommendedRows({
        warnSectionIds: this.warnings.map((s) => s.id),
        mustFixSectionIds,
        hasClient: this.campaign?.client_id != null,
        info: this.readinessPayload?.info,
        gates: this.launchGates,
        mustFixGateCodes: new Set(this.launchReasons.map((r) => r.code)),
        answered: true,
      });
    },
    // Sections the editor flags as unfinished: guidance. Which of them are
    // also launch blockers is the server's call (the Must fix list).
    warnings() {
      return ALL_SECTIONS.filter(s => this.sectionStatus[s.id] === 'warn');
    },
    hasBodyContent() {
      return this.readinessError
        || this.readinessLoading
        || this.mustFixRows.length > 0
        || this.attentionRows.length > 0
        || this.recommendedList.length > 0;
    },
    doneCount() {
      return ALL_SECTIONS.filter(s => this.sectionStatus[s.id] === 'done').length;
    },
    percentComplete() {
      const total = ALL_SECTIONS.length;
      return total ? Math.round((this.doneCount / total) * 100) : 0;
    },
    circumference() {
      return 2 * Math.PI * 36; // ~226.2
    },
    dashOffset() {
      return this.circumference * (1 - this.percentComplete / 100);
    },
    // THE server answer to "can this store go live now?" -- the ``launch``
    // block of the readiness payload (state_transition_guard
    // .compute_store_launch_eligibility). The Stores list toggle's
    // pre-flight reads the same block, so the two cannot disagree. Nothing
    // here re-derives it: null until the server has answered.
    //
    // FRESH-ONLY: the store hands the block back only when no read is in
    // flight and the last one succeeded (storeReadiness.launchAnswerFor), so
    // a payload cached before a close, a save or a launch (the Stores list
    // pre-flight fills the same cache) is never shown as current.
    launchAnswer() {
      const storeId = this.campaign?._jv?.id;
      if (storeId == null) return null;
      return useStoreReadinessStore().launchAnswerFor(storeId);
    },
    readinessLoading() {
      const storeId = this.campaign?._jv?.id;
      return storeId != null && useStoreReadinessStore().isLoading(storeId);
    },
    readinessError() {
      const storeId = this.campaign?._jv?.id;
      return storeId != null && !!useStoreReadinessStore().errorFor(storeId);
    },
    isLive() {
      return this.launchAnswer?.live === true;
    },
    // Enabled ONLY on the server's yes. Before it answers (or when the
    // readiness read failed) the button stays disabled: an unverified
    // launch is not offered.
    canLaunch() {
      return this.launchAnswer?.can_launch === true;
    },
    launchReasons() {
      const reasons = this.launchAnswer?.reasons;
      return Array.isArray(reasons) ? reasons : [];
    },
    launchDisabledTooltip() {
      if (!this.launchAnswer) {
        return this.readinessError
          ? "Launch readiness could not be checked. Reload the page to try again."
          : "Checking whether this store can launch...";
      }
      // The server's own reason text. Section reasons carry the editor tab
      // keys; name the sections so the reason says where to go.
      return this.launchReasons
        .map((r) => {
          const titles = unreadySections(r.tabs).map((sec) => sec.title);
          return titles.length ? `${r.message} (${titles.join(", ")})` : r.message;
        })
        .filter(Boolean)
        .join(" ");
    },
    bannerHeadline() {
      if (this.isLive) return "Store is live";
      if (this.canLaunch) return "Ready to launch";
      if (!this.launchAnswer) {
        return this.readinessError
          ? "Launch readiness could not be checked"
          : "Checking launch readiness";
      }
      return "Not ready to launch yet";
    },
    bannerSubline() {
      if (this.isLive) {
        return "This store has been launched. You can close it from the Stores list.";
      }
      if (this.canLaunch) {
        return "Click Launch Store to open the store to shoppers.";
      }
      // Before the server answers, section progress is the only number
      // there is (guidance, never the launch decision). Once it answers, the
      // count is the Must fix list's.
      if (!this.launchAnswer) {
        const remaining = ALL_SECTIONS.length - this.doneCount;
        return remaining > 0
          ? `${remaining} section${remaining === 1 ? "" : "s"} left to fill in.`
          : "";
      }
      // The count is the number of rows under "Must fix to launch", so the
      // headline and the list cannot disagree.
      const n = this.mustFixRows.length;
      // A "no" with no reasons should not happen; never say "0 things".
      if (n === 0) return "This store cannot launch yet. See the details below.";
      return `${n} thing${n === 1 ? "" : "s"} to fix before this store can go live.`;
    },
    bannerClass() {
      if (this.isLive || this.canLaunch) return "crl__banner--ready";
      return "crl__banner--default";
    },
  },
  methods: {
    async loadReadiness(storeId) {
      // The store is the sink and the only copy: it keys the payload, the
      // loading flag and the error by storeId (``readinessPayload``,
      // ``readinessLoading``, ``readinessError`` and ``launchAnswer`` read
      // them back), and its request token drops a late response for an
      // older read. Nothing is mirrored into local state, so nothing here
      // can go stale or show another store's answer.
      try {
        await useStoreReadinessStore().fetchReadiness(storeId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Review & Launch: readiness fetch failed", describeError(err));
      }
    },
    goToGate(route) {
      // The route comes from the server-side evaluator; only follow an
      // in-app path so a bad payload can never navigate off-site.
      if (typeof route === "string" && route.startsWith("/")) {
        this.$router.push(route);
      }
    },
    // A row's Fix link: a section tab, an in-app page or the Approval
    // Workspace. goToGate refuses anything that is not an in-app path.
    followFix(fix) {
      if (!fix) return;
      if (fix.type === "section") this.$emit("go-to-section", fix.id);
      else if (fix.type === "route") this.goToGate(fix.route);
      else if (fix.type === "workspace") this.goToApprovalWorkspace();
    },
    goToApprovalWorkspace() {
      const sid = this.campaign?._jv?.id;
      if (!sid) return;
      this.$router.push({
        path: "/distributor/decorations/approvals",
        query: { filter: `store:${sid}` },
      });
    },
    cardClass(card) {
      const status = this.sectionStatus[card.id];
      return {
        'crl__card--done': status === 'done',
        'crl__card--warn': status === 'warn',
      };
    },
    statusClass(card) {
      const status = this.sectionStatus[card.id];
      if (status === 'done') return 'crl__status--done';
      if (status === 'warn') return 'crl__status--warn';
      return 'crl__status--pending';
    },
    statusLabel(card) {
      const status = this.sectionStatus[card.id];
      if (status === 'done') return 'Complete';
      if (status === 'warn') return 'Needs attention';
      return 'Not started';
    },
    cardSubtitle(card) {
      const sub = this.sectionSubtitle && this.sectionSubtitle[card.id];
      return (typeof sub === 'string' && sub.trim()) ? sub : '';
    },
  },
};
</script>

<style scoped>
.crl { font-family: var(--tf-font-sans, 'Roboto', sans-serif); }

/* One block: the headline banner on top, the two lists beneath it. */
.crl__status {
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 28px;
  background: var(--tf-bg);
}
.crl__status-body { padding: 20px 28px 24px; }
.crl__status-body--empty { padding: 0; }
.crl__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.crl__banner {
  background: linear-gradient(135deg, var(--tf-secondary) 0%, #1f5089 100%);
  color: var(--tf-bg);
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 24px;
}
.crl__banner--ready {
  background: linear-gradient(135deg, #00875a 0%, var(--tf-success) 100%);
}
.crl__donut { position: relative; width: 84px; height: 84px; flex-shrink: 0; }
.crl__donut-pct {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font: 600 22px var(--tf-font-display, 'Poppins'), sans-serif;
  color: var(--tf-bg);
}
.crl__copy { flex: 1; }
.crl__h2 {
  font: 500 22px var(--tf-font-display, 'Poppins'), sans-serif;
  margin: 0 0 4px;
  color: var(--tf-bg);
}
.crl__sub {
  font: 400 14px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: #cfd8e3;
  margin: 0;
  line-height: 1.5;
}
.crl__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}
.crl__launch {
  border: none;
  border-radius: 9999px;
  padding: 10px 22px;
  font: 500 14px var(--tf-font-sans, 'Roboto'), sans-serif;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  background: var(--tf-success, var(--tf-success));
  color: var(--tf-bg);
}
/* Disabled must read as DISABLED at a glance, not as "slightly different
   green". The previous #5a7c70 was a desaturated green at full opacity, so
   against the dark status panel it still looked like a live CTA -- the only
   real signal was cursor:not-allowed, which requires hovering to discover
   (reported by Jake 2026-08-12 while verifying the Review & Launch card).
   Neutral slate + reduced opacity removes the brand hue entirely, so the
   affordance is legible without hovering and without relying on color
   perception alone. */
.crl__launch:disabled {
  background: #6b7785;
  color: #e8edf2;
  opacity: 0.65;
  cursor: not-allowed;
}
.crl__launch .tf-icon { font-size: 18px; }

/* A live store's status: full-strength, NOT a disabled control. White on
   the green banner, with an icon, so it reads without color alone. */
.crl__live {
  border-radius: 9999px;
  padding: 10px 22px;
  font: 500 14px var(--tf-font-sans, 'Roboto'), sans-serif;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  background: var(--tf-bg);
  color: var(--tf-success-dark, #00664a);
}
.crl__live .tf-icon { font-size: 18px; }

.crl__preview {
  border: none;
  border-radius: 9999px;
  padding: 10px 22px;
  font: 500 14px var(--tf-font-sans, 'Roboto'), sans-serif;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  background: rgba(255,255,255,.1);
  color: var(--tf-bg);
}
.crl__preview:hover { background: rgba(255,255,255,.18); }
.crl__preview .tf-icon { font-size: 18px; }

.crl__link {
  color: var(--tf-primary, var(--tf-primary));
  font-weight: 500;
  cursor: pointer;
  margin-left: 6px;
}
.crl__link:hover { text-decoration: underline; }

.crl__readiness-error {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--tf-error-soft-alt, #fdf1f1);
  border: 1px solid rgba(225, 50, 50, 0.35);
  border-radius: 10px;
  padding: 16px 20px;
  color: var(--tf-fg-1);
}
.crl__readiness-error .tf-icon {
  color: var(--tf-error-dark, #9c1b1b);
  font-size: 20px;
  margin-top: 2px;
}
.crl__readiness-error-sub {
  margin: 4px 0 0;
  font: 400 13px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-2);
  line-height: 1.5;
}

.crl__section-list-head { margin: 32px 0 14px; }
.crl__h2-sm {
  font: 500 18px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-secondary, var(--tf-secondary));
  margin: 0;
}

.crl__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.crl__card {
  background: var(--tf-bg);
  border: 1px solid var(--tf-border, var(--tf-border));
  border-radius: 10px;
  padding: 18px 20px;
  cursor: pointer;
  transition: all .15s;
  position: relative;
  text-decoration: none;
  color: inherit;
  display: block;
}
.crl__card:hover {
  border-color: var(--tf-primary, var(--tf-primary));
  box-shadow: 0 2px 8px rgba(17,154,233,.08);
  transform: translateY(-1px);
}
.crl__card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.crl__card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--tf-surface-light, var(--tf-surface-light));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tf-fg-4);
}
.crl__card-icon .tf-icon { font-size: 18px; }
.crl__card--done .crl__card-icon { background: var(--tf-chip-success-bg); color: var(--tf-success-dark); }
.crl__card--warn .crl__card-icon { background: var(--tf-error-soft-alt); color: var(--tf-error-dark); }
.crl__card-h {
  font: 500 15px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-1);
  flex: 1;
}
.crl__card-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  font: 600 10px var(--tf-font-sans, 'Roboto'), sans-serif;
  text-transform: uppercase;
  letter-spacing: .8px;
}
.crl__status--done    { background: var(--tf-chip-success-bg); color: var(--tf-success-dark); }
.crl__status--pending { background: var(--tf-surface-light, var(--tf-surface-light)); color: var(--tf-fg-4); }
.crl__status--warn    { background: var(--tf-error-soft-alt); color: var(--tf-error-dark); }

.crl__card-sub {
  font: 400 12px var(--tf-font-sans, 'Roboto'), sans-serif;
  color: var(--tf-fg-4);
  line-height: 1.4;
  margin-top: 2px;
  margin-right: 28px;
}

.crl__card-arrow {
  position: absolute;
  right: 18px;
  bottom: 16px;
  color: var(--tf-fg-4);
  font-size: 18px;
}
.crl__card:hover .crl__card-arrow { color: var(--tf-primary, var(--tf-primary)); }

@media (max-width: 720px) {
  .crl__banner { flex-direction: column; align-items: flex-start; }
  .crl__actions { align-items: flex-start; width: 100%; }
  .crl__grid { grid-template-columns: 1fr; }
}
</style>
